import React, { useContext, useEffect, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { TextileAppState } from 'api/textile_deals/fetchTextileAppState';
import ButtonBlock from 'blocks/ButtonBlock';
import TopActionBlock from 'blocks/TopActionBlock';
import Divider from 'components/Divider/Divider';
import GenericErrorComponent from 'components/InfoBoxComponent/GenericErrorComponent';
import LoadingIndicatorComponent from 'components/LoadingIndicatorComponent';
import PageHeader from 'components/PageHeader/PageHeader';
import PageStackBlock from 'components/PageStackBlock/PageStackBlock';
import { HasAccessContext } from 'contexts/HasAccessContext';
import BackLink from 'domain/Links/BackLink';
import Headline from 'elements/Headline';
import { intl } from 'i18n';
import { useSelectedTextileVariantQuery } from 'queries/textile_deals/useSelectedTextileVariantQueries';
import { useTextileOrderForPreorderQuery } from 'queries/textile_deals/useTextileOrderQueries';
import {
  useManualUpdateTexitlePreorderQuery,
  useRemoveManualTextilePreorderQuery,
  useTextilePreoderQuery
} from 'queries/textile_deals/useTextilePreorderQueries';
import { FormattedMessage } from 'react-intl';
import InfoTextoForTextileComponent from 'screens/preorders/components/InfoTextForTextileComponent';
import OrganizerMessageComponent from 'screens/preorders/components/OrganizerMessageComponent';
import PreordersFormErrorsComponent from 'screens/preorders/components/PreorderFormErrorsComponent';
import PreorderNameFieldComponent from 'screens/preorders/components/PreorderNameFieldComponent';
import SizesByTextileComponent from 'screens/preorders/components/SizesByTextileVariantComponent';
import { HistoryProps, idFromMatch } from 'utils/history';
import { textileOrderIdFromMatch } from 'utils/history/textile-order-id-from-match';
import { textileOrderRoute } from 'utils/history/textile-order-route';
import useForm, { FormType } from 'utils/hooks/useForm';

interface FormProps {
  form: FormType;
}

const PreorderVirtualEditScreen: React.FC<HistoryProps & FormProps> = (
  props
) => {
  const [errors, setErrors] = useState<string[]>([]);
  const textileOrderId = textileOrderIdFromMatch(props.match);
  const textilePreorderId = idFromMatch(props.match);
  const accessContext = useContext(HasAccessContext);
  const queryClient = useQueryClient();
  const textileAppState = queryClient.getQueryData<TextileAppState>([
    'textile_app_state',
    textileOrderId
  ]);
  const textileOrder = useTextileOrderForPreorderQuery(textileOrderId);
  const textilePreorder = useTextilePreoderQuery(
    textilePreorderId,
    textileOrderId
  );
  const selectedTextileVariant = useSelectedTextileVariantQuery(textileOrderId);
  const updateTextilePreorder = useManualUpdateTexitlePreorderQuery(
    textileOrderId,
    textilePreorderId
  );
  const removeTextilePreoder = useRemoveManualTextilePreorderQuery(
    textileOrderId
  );

  if (!accessContext) {
    throw Error('Component must be used within HasAccessContextProvider');
  }

  useEffect(() => {
    if (textileAppState?.textile_order.order_state === 'preparing') {
      props.history.replace(textileOrderRoute(props.match, '/preorder'));
    }

    const data = textilePreorder.data;
    if (data) {
      props.form.setField('name', data.preorder_name);
      props.form.setField('size', data.textile_size_id?.toString());
    }

    accessContext.checkSectionAccess('preorder');

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    accessContext,
    textileAppState?.textile_order.order_state,
    textilePreorder.data
  ]);

  const renderPage = (content?: React.ReactNode) => {
    return (
      <>
        <TopActionBlock>
          <BackLink to={textileOrderRoute(props.match, '/preorder')} />
        </TopActionBlock>

        <PageStackBlock>
          <PageHeader
            headline={<FormattedMessage id={'preorder add manuell'} />}
            text={''}
          />
          {content}
        </PageStackBlock>
      </>
    );
  };

  if (textileOrder.isError) {
    return renderPage(
      <GenericErrorComponent onRetryClick={() => textileOrder.refetch()} />
    );
  }

  if (selectedTextileVariant.isError) {
    return renderPage(
      <GenericErrorComponent
        onRetryClick={() => selectedTextileVariant.refetch()}
      />
    );
  }

  if (textilePreorder.isError) {
    return renderPage(
      <GenericErrorComponent onRetryClick={() => textilePreorder.refetch()} />
    );
  }

  if (
    props.form.loading ||
    !textileAppState ||
    textilePreorder.isLoading ||
    !textilePreorder.data ||
    textileOrder.isLoading ||
    !textileOrder.data ||
    selectedTextileVariant.isLoading ||
    !selectedTextileVariant.data
  ) {
    return renderPage(<LoadingIndicatorComponent />);
  }

  const removePreorderManual = () => {
    const { form } = props;

    if (!textilePreorderId || !textileOrderId) {
      return;
    }

    form.setLoading(true);
    removeTextilePreoder.mutate(
      { id: textilePreorderId, textileOrderId },
      {
        onSettled: () => {
          form.setLoading(false);
          props.history.push(textileOrderRoute(props.match, '/preorder'));
        }
      }
    );
  };

  const updateOrderManual = () => {
    const { form, match, history } = props;

    if (!textileOrderId || !textilePreorderId) {
      return;
    }

    const currentErrors = [];

    if (!form.values.size) {
      currentErrors.push(
        intl.formatMessage({ id: 'preorder add missing size' })
      );
      setErrors(currentErrors);
    }

    if (!form.values.name) {
      currentErrors.push(
        intl.formatMessage({ id: 'preorder add missing name' })
      );
      setErrors(currentErrors);
    }

    if (form.values.size && form.values.name) {
      const patch = {
        textile_size_id: form.values.size,
        name: form.values.name
      };
      form.setLoading(true);
      updateTextilePreorder.mutate(
        { id: textilePreorderId, data: patch, textileOrderId },
        {
          onSettled: () => {
            form.setLoading(false);
            history.push(textileOrderRoute(match, '/preorder'));
          }
        }
      );
    }
  };

  const renderDeleteButton = () => {
    return (
      <ButtonBlock
        background="RED_LIGHT"
        color="RED"
        onClick={() => removePreorderManual()}
      >
        <FormattedMessage id="Remove" />
      </ButtonBlock>
    );
  };

  const renderAddButton = () => {
    return (
      <ButtonBlock background="PRIMARY" onClick={() => updateOrderManual()}>
        <FormattedMessage id="Save" />
      </ButtonBlock>
    );
  };

  const renderAdd = () => {
    return renderPage(
      <>
        <Divider />
        <Headline.Medium>
          <FormattedMessage id="Your Order" />
        </Headline.Medium>
        <OrganizerMessageComponent
          organizerMessage={textileOrder.data.organizer_preorder_message}
        />
        <InfoTextoForTextileComponent
          size_info={selectedTextileVariant.data.textile_size_info}
        />
        <PreorderNameFieldComponent form={props.form} />
        <SizesByTextileComponent
          form={props.form}
          sizes={selectedTextileVariant.data.textile_sizes}
        />
        <PreordersFormErrorsComponent errors={errors} />
        {renderAddButton()}
        <Divider />
        {renderDeleteButton()}
      </>
    );
  };

  return renderAdd();
};

export default (props: HistoryProps) => {
  const form = useForm();
  // @ts-ignore
  return <PreorderVirtualEditScreen {...props} form={form} />;
};
